/* eslint-disable */
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import { Form } from '@entrata/portals-common/lib/components/forms';
import FormActionBar from 'components/FormActionBar';
import Message from 'components/Message';
import { Btn } from 'components/buttons';
import { X } from '@entrata/portals-common/lib/icons';
import { AuthContext } from 'components/AuthProvider';
import { navigate } from 'gatsby';
import PageShell from 'components/page-shell';
import styled from '@emotion/styled';
import logoHomebody from 'images/homebody-logo-emerald.svg';
import { WHITE } from 'constants/theme';
import loginBanner1x from 'images/Login-banner-1x.png';
import loginBanner2x from 'images/Login-banner-2x.png';
import { getQuoteVersionVariant } from 'api/digitalAnalytics';
import { QUOTE_VERSION_V2 } from 'constants/common';
import { requestToken } from 'redux/modules/user';
import { setStateAtStep, setActiveStep } from 'redux/modules/getQuote';

const msgStyle = { marginBottom: '16px' };

const StyledDiv = styled('div')`
  width: 296px;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.3);
  padding: 20px;
  margin: 0 auto;
  background: ${WHITE};
  position: relative;
`;

const StyledH1 = styled('h1')`
  text-align: center;
  color: ${WHITE};
  margin: 0;
  padding: 24px 0;
  font-family: ${({ theme }) => theme.fonts.algebraLight};

  @media only screen and (max-width: 767px) {
    padding: 32px 0 32px;
    margin-bottom: 0;
  }
`;
const StyledH2 = styled('h2')`
  text-align: center;
  color: ${({ theme }) => `${theme.secondary.dark} !important`};
  margin: 8px;
`;

const StyledH5 = styled('h5')`
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: 12px;
  text-align: left;
  color: ${({ theme }) => theme.text.dark};
`;

const StyledBtn = styled(Btn)`
  width: 200px;
  font-family: ${({ theme }) => theme.fonts.soehneKraftig};
`;

const StyledFormActionBar = styled(FormActionBar)`
  text-align: center;
  padding-top: 16px;
`;

const StyledImgWrapper = styled('div')`
  text-align: center;
  margin-bottom: 24px;

  > a {
    color: ${({ theme }) => theme.text.medium};
    position: absolute;
    right: 16px;
    top: 16px;
  }
`;

const StyledLogoImg = styled('img')`
  width: 144px;
`;

const StyledSection = styled('section')`
  background-image: url(${loginBanner1x});
  background-image: -webkit-image-set(url(${loginBanner1x}) 1x, url(${loginBanner2x}) 2x);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;

  @media only screen and (max-width: 462px) {
    min-height: calc(100vh);
  }

  @media only screen and (max-width: 768px) {
    min-height: calc(100vh - 100px);
  }

  @media only screen and (min-width: 769px) {
    min-height: calc(100vh - 125px);
  }
`;

const LoginBox = ({
  children,
  initialFieldState,
  fieldStateReducer = {},
  location,
  onSubmit,
  pageTitle,
  title,
  subTitle,
  btnText,
  type,
  message,
  successMessage = '',
  allowClose,
  isVerified = true,
  setStateAtStep,
  setActiveStep,
  requestToken,
  homebody2WebUrl,
}) => {
  const [quoteVersion, setQuoteVersionValue] = useState(QUOTE_VERSION_V2);

  const setQuoteVersion = async () => {
    setQuoteVersionValue(await getQuoteVersionVariant());
  };

  useEffect(() => {
    setQuoteVersion();
    requestToken({
      key: '',
      url: location.host,
      onRequestFail: () => {
        navigate(`/get-quote-${quoteVersion}/`);
        setStateAtStep({ step: 1, complete: false, ignore: false, stepAccessible: true });
        setActiveStep({ activeStep: 1 });
      },
    });
  }, []);

  return (
    <AuthContext.Consumer>
      {({ authenticated }) => {
        if (authenticated) {
          navigate('/app/policies/');
          return null;
        }

        return (
          <PageShell location={location} quoteVersion={quoteVersion}>
            <StyledSection>
              <Helmet>
                <title>{title || pageTitle}</title>
              </Helmet>
              <div>
                <StyledH1 style={{ color: WHITE }}>{pageTitle}</StyledH1>
                <StyledDiv message={!!message}>
                  <StyledImgWrapper>
                    <StyledLogoImg src={logoHomebody} />
                    {allowClose && (
                      <Link to='/login/'>
                        <X size={24} />
                      </Link>
                    )}
                  </StyledImgWrapper>
                  {title && <StyledH2>{title}</StyledH2>}
                  {subTitle && <StyledH5>{subTitle}</StyledH5>}
                  {(!!message || !!successMessage) && (
                    <Message inline show={true} type={successMessage ? 'success' : type} style={msgStyle}>
                      {message || successMessage}
                    </Message>
                  )}
                  {((type == 'info' && !message) || type == 'error') && (
                    <Fragment>
                      <Form
                        initialFieldState={initialFieldState}
                        fieldStateReducer={fieldStateReducer}
                        onSubmit={onSubmit}
                      >
                        {children}
                        <Form.Actions>
                          {({ hasErrors }) => (
                            <StyledFormActionBar>
                              <StyledBtn appearance='primary' type='submit' disabled={hasErrors || !isVerified}>
                                {btnText}
                              </StyledBtn>
                            </StyledFormActionBar>
                          )}
                        </Form.Actions>
                      </Form>
                    </Fragment>
                  )}
                </StyledDiv>
              </div>
            </StyledSection>
          </PageShell>
        );
      }}
    </AuthContext.Consumer>
  );
};

LoginBox.propTypes = {
  children: PropTypes.any,
  initialFieldState: PropTypes.object,
  fieldStateReducer: PropTypes.object,
  location: PropTypes.object,
  onSubmit: PropTypes.func,
  pageTitle: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  btnText: PropTypes.string,
  message: PropTypes.string,
  successMessage: PropTypes.string,
  type: PropTypes.string,
  allowClose: PropTypes.bool,
  requestToken: PropTypes.func,
  setStateAtStep: PropTypes.func,
  setActiveStep: PropTypes.func,
  homebody2WebUrl: PropTypes.string,
};

export default connect(
  ({ user }) => ({
    homebody2WebUrl: user.homebody2WebUrl,
  }),
  { requestToken, setStateAtStep, setActiveStep },
)(LoginBox);
